import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { SyncErrorClassification } from '../../actions/plaid/authActions'

export enum AccountTypes {
  CREDIT = 'credit',
  DEPOSITORY = 'depository',
}

export enum FinancialType {
  PLAID = 'plaid',
  MANUAL = 'manual',
  USER_MANUAL_UPLOAD = 'user_manual_upload',
}

export enum TransactionSyncState {
  ENABLED = 'enabled',
  DISABLED = 'disabled',
  PENDING = 'pending',
}

export enum FinancialAccountPrimaryUse {
  BUSINESS = 'business',
  PERSONAL = 'personal',
}

export enum PrimaryUseEffectiveSource {
  BOOKKEEPER = 'bookkeeper',
  THERAPIST_ANSWER = 'therapist_answer',
  INFERRED = 'inferred',
}

export interface FinancialAccount {
  FinancialProfileId: number
  accountId: string
  accountType: string
  accounts: {
    userId: number
    errorObject: null | {
      error_code: string
      error_message: string
    }
    needsReconnection: boolean
    disconnectedOn?: string
    markedCannotReconnectAt: string
    syncErrorClassification: SyncErrorClassification | null
    inactive?: boolean
  }
  createdAt: string
  financialProfileId: number
  id: number
  inactive: boolean
  mask: string | null
  name: string
  plaidInstitutionId: string | null
  plaidInstitutionName: string | null
  plaidItemId: number | null
  subtype: string
  type: AccountTypes | null
  updatedAt: string
  markedPersonalAt: string | null
  manualAccount: {
    name: string
    inactive: boolean
  } | null
  isHidden: boolean | null
  transactionSyncState: TransactionSyncState | null
  statementPermissions: string | null
  onboardingAnswers: {
    [identifier: string]: boolean
  }
  hasPersonalTransactions: boolean
  hasBusinessTransactions: boolean | null
  primaryUse: FinancialAccountPrimaryUse | null
  primaryUseSetAt: string | null
  primaryUseEffective: FinancialAccountPrimaryUse | null
  primaryUseEffectiveSource: PrimaryUseEffectiveSource | null
  bankAccessEnabledAt: string | null
  excludeFromReports: boolean
  inactivatedAt: string | null
  currentBalanceInCents: number | null
  balanceEffectiveDate: string | null
  lastTransactionDate: string | null
  unreconciledTransactionCount?: number
}

/** The categorization an account is reactivated into. */
export type ActivateAs = 'business' | 'personal'

export interface FinancialAccountState {
  [key: number]: FinancialAccount
}

const preserveUnreconciledTransactionCount = (
  account: FinancialAccount,
  previous?: FinancialAccount
): FinancialAccount => {
  if (
    account.unreconciledTransactionCount !== undefined ||
    previous?.unreconciledTransactionCount === undefined
  ) {
    return account
  }

  return {
    ...account,
    unreconciledTransactionCount: previous.unreconciledTransactionCount,
  }
}

const financialAccountsSlice = createSlice({
  name: 'financialAccounts',
  initialState: {} as FinancialAccountState,
  reducers: {
    receiveFinancialAccounts: (
      state,
      action: PayloadAction<{ [key: number]: FinancialAccount }>
    ) => {
      const next: FinancialAccountState = {}
      Object.values(action.payload).forEach((account) => {
        next[account.id] = preserveUnreconciledTransactionCount(
          account,
          state[account.id]
        )
      })
      return next
    },
    receiveCreateFinancialAccount: (
      state,
      action: PayloadAction<FinancialAccount>
    ) => {
      return {
        ...state,
        [action.payload.id]: action.payload,
      }
    },
    receiveDeleteFinancialAccount: (state, action: PayloadAction<number>) => {
      delete state[action.payload]
    },
    receiveUpdateFinancialAccount: (
      state,
      action: PayloadAction<FinancialAccount>
    ) => {
      state[action.payload.id] = action.payload
    },
    receiveDeactivateFinancialAccount: (
      state,
      action: PayloadAction<number>
    ) => {
      const account = state[action.payload]
      if (account) {
        state[action.payload] = {
          ...account,
          inactive: true,
          inactivatedAt: new Date().toISOString(),
          transactionSyncState: TransactionSyncState.DISABLED,
        }
      }
    },
    receiveReactivateFinancialAccount: (
      state,
      action: PayloadAction<{
        financialAccountId: number
        activateAs: ActivateAs
      }>
    ) => {
      const { financialAccountId, activateAs } = action.payload
      const account = state[financialAccountId]
      if (account) {
        state[financialAccountId] = {
          ...account,
          inactive: false,
          inactivatedAt: null,
          transactionSyncState: TransactionSyncState.ENABLED,
          ...(activateAs === 'business' ? { markedPersonalAt: null } : {}),
        }
      }
    },
  },
})

export default financialAccountsSlice.reducer

export const {
  receiveFinancialAccounts,
  receiveCreateFinancialAccount,
  receiveDeleteFinancialAccount,
  receiveDeactivateFinancialAccount,
  receiveReactivateFinancialAccount,
  receiveUpdateFinancialAccount,
} = financialAccountsSlice.actions
